"use client";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable,
    Header,
    HeaderContext,
} from "@tanstack/react-table";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "~/components/shadcn/Table";
import { cn } from "~/utils/fontsConfig";
import { useBreakpointV3 } from "~/hooks/useIsMobile";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { SponsorshipPackageTier } from "@prisma/client";

interface TableMeta {
    selectedIds?: Set<string>;
    selectedTier?: SponsorshipPackageTier | null;
    onSelectionChange?: (eventId: string) => void;
}

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
    className?: string;
    showBorders?: boolean;
    meta?: TableMeta;
}

export function DataTable<TData, TValue>({
    columns,
    data,
    className,
    showBorders = true,
    meta,
}: DataTableProps<TData, TValue>) {
    const isMobile = !useBreakpointV3("sm");
    const [parent] = useAutoAnimate();

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        meta,
    });

    if (isMobile) {
        return (
            <div className="flex w-full flex-col gap-2" ref={parent}>
                {table.getRowModel().rows.map((row) => (
                    <div
                        key={row.id}
                        className={cn(
                            "rounded bg-v3-N-400 bg-opacity-30 p-4",
                            showBorders && "border border-[#23272B]",
                        )}
                    >
                        {row.getVisibleCells().map((cell) => {
                            const headerValue =
                                typeof cell.column.columnDef.header ===
                                "function"
                                    ? cell.column.columnDef.header({
                                          header: cell.column
                                              .columnDef as Header<
                                              TData,
                                              unknown
                                          >,
                                          column: cell.column,
                                          table,
                                      } as HeaderContext<TData, unknown>)
                                    : cell.column.columnDef.header;

                            return (
                                <div
                                    key={cell.id}
                                    className="mb-2 flex flex-col last:mb-0"
                                >
                                    {headerValue && headerValue !== "" && (
                                        <span className="mb-1 text-sm text-v3-N-300">
                                            {headerValue}
                                        </span>
                                    )}
                                    <div className="text-base">
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext(),
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}

                {table.getRowModel().rows.length === 0 && (
                    <div className="py-4 text-center text-v3-N-300">
                        No results.
                    </div>
                )}
            </div>
        );
    }

    return (
        <div
            className={cn(
                "w-full",
                showBorders && "rounded-md border border-[#23272B]",
            )}
        >
            <Table className={cn("max-h-[600px]", className)}>
                <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow
                            key={headerGroup.id}
                            className={cn(
                                showBorders && "border-b border-[#23272B]",
                            )}
                        >
                            {headerGroup.headers.map((header, index) => (
                                <TableHead key={header.id}>
                                    {showBorders &&
                                        index !== 0 &&
                                        header.column.columnDef.header !==
                                            "" && (
                                            <span className="absolute left-0 top-[25%] h-[50%] w-[1px] rounded-full bg-[#23272B]" />
                                        )}
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                              header.column.columnDef.header,
                                              header.getContext(),
                                          )}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map((row) => (
                            <TableRow
                                key={row.id}
                                data-state={row.getIsSelected() && "selected"}
                                className={cn(
                                    showBorders && "border-b border-[#23272B]",
                                )}
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell key={cell.id}>
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext(),
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell
                                colSpan={columns.length}
                                className="h-24 text-center"
                            >
                                No results.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
}

// declare module "@tanstack/table-core" {
//
// }
